/**
 * Savoy & Co. Delivery Tracker
 * Shopify theme asset: savoy-delivery-tracker.js
 * Version 2026-10-17b
 */

(() => {
  'use strict';

  const PROXY = '/apps/savoy-delivery';
  const CONTAINER = '.bcdp-zip-container';
  const INPUT = '.bcdp-zip-wrap input.bcdp-widget-input';
  const ERROR = '.bcdp-error-msg';
  const BOX_ID = 'savoy-custom-delivery-panel';

  let lastAttemptKey = '';
  let pendingCheck = false;
  let busy = false;

  const seen = new Set();

  const newId = () =>
    'savoy_' + (
      crypto.randomUUID
        ? crypto.randomUUID().replaceAll('-', '')
        : Date.now().toString(36) +
          Math.random().toString(36).slice(2)
    );

  async function cartPayload(zip) {
    const response = await fetch('/cart.js', {
      credentials: 'same-origin',
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error('Unable to load cart');
    }

    const cart = await response.json();

    return {
      zip,
      items: cart.items.map(item => ({
        title:
          item.product_title ||
          item.title ||
          '',
        variant: item.variant_title || '',
        quantity: item.quantity
      })),
      subtotalCents: cart.items_subtotal_price,
      currency: cart.currency || 'USD'
    };
  }

  async function submit(payload) {
    const response = await fetch(PROXY, {
      method: 'POST',
      credentials: 'same-origin',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(payload)
    });

    let result;

    try {
      result = await response.json();
    } catch (_) {
      throw new Error('Unexpected response');
    }

    if (!response.ok || result.success !== true) {
      throw new Error(
        result.error || 'Submission unavailable'
      );
    }

    return result;
  }

  function field(
    type,
    name,
    placeholder,
    required = false
  ) {
    const el = document.createElement('input');

    el.type = type;
    el.name = name;
    el.placeholder = placeholder;

    el.autocomplete =
      name === 'name'
        ? 'name'
        : name === 'email'
          ? 'email'
          : 'tel';

    el.required = required;

    el.maxLength =
      name === 'email'
        ? 254
        : name === 'phone'
          ? 40
          : 120;

    el.style.cssText = `
      box-sizing: border-box;
      min-height: 48px;
      width: 100%;
      padding: 12px 14px;
      border: 1px solid #c8b5a2;
      border-radius: 4px;
      background: #fff;
      font: inherit;
    `;

    return el;
  }

  function buildPanel(container) {
    const old = document.getElementById(BOX_ID);

    if (
      old &&
      old.closest(CONTAINER) === container
    ) {
      return old;
    }

    if (old) old.remove();

    const panel = document.createElement('section');
    panel.id = BOX_ID;

    panel.style.cssText = `
      background: #f5e8d8;
      color: #30211b;
      border: 1px solid #d7bca2;
      border-radius: 4px;
      margin: 16px 0;
      padding: 20px;
      box-sizing: border-box;
    `;

    const title = document.createElement('h3');

    title.textContent =
      'CUSTOMIZED DELIVERY MAY BE AVAILABLE';

    title.style.cssText = `
      font-size: 18px;
      line-height: 1.3;
      font-weight: 700;
      margin: 0 0 12px;
    `;

    const message = document.createElement('p');

    message.textContent =
      'Your order deserves special attention. ' +
      'We may be able to accommodate delivery ' +
      'beyond our regular service area. ' +
      'Submit your request below, and our team ' +
      'will be in touch promptly to explore ' +
      'the available arrangements.';

    message.style.cssText = `
      text-transform: none !important;
      font-size: 14px;
      line-height: 1.6;
      font-weight: 400;
      margin: 0 0 16px;
    `;

    const form = document.createElement('form');

    form.style.cssText = `
      display: grid;
      gap: 10px;
    `;

    const name = field(
      'text',
      'name',
      'Your name',
      true
    );

    const email = field(
      'email',
      'email',
      'Email address',
      true
    );

    const phone = field(
      'tel',
      'phone',
      'Phone number (optional)'
    );

    const consentLabel =
      document.createElement('label');

    consentLabel.style.cssText = `
      display: flex;
      gap: 9px;
      align-items: flex-start;
      font-size: 12px;
      line-height: 1.5;
    `;

    const consent = document.createElement('input');
    consent.type = 'checkbox';
    consent.required = true;

    consent.style.cssText = `
      margin-top: 3px;
      flex: 0 0 auto;
    `;

    consentLabel.append(
      consent,
      document.createTextNode(
        'I agree that Savoy & Co. may contact ' +
        'me about this customized delivery request.'
      )
    );

    const button = document.createElement('button');
    button.type = 'submit';

    button.textContent =
      'REQUEST CUSTOMIZED DELIVERY';

    button.style.cssText = `
      min-height: 48px;
      padding: 12px;
      background: #584942;
      color: white;
      border: 0;
      border-radius: 4px;
      cursor: pointer;
      font: inherit;
    `;

    const status = document.createElement('p');

    status.setAttribute('role', 'status');
    status.setAttribute('aria-live', 'polite');

    status.style.cssText = `
      margin: 0;
      font-size: 13px;
      line-height: 1.5;
    `;

    const caveat = document.createElement('p');

    caveat.textContent =
      'Customized delivery is subject to ' +
      'availability and may involve an ' +
      'additional delivery fee.';

    caveat.style.cssText = `
      font-size: 11px;
      line-height: 1.5;
      margin: 0;
    `;

    form.append(
      name,
      email,
      phone,
      consentLabel,
      button,
      status,
      caveat
    );

    panel.append(
      title,
      message,
      form
    );

    const errorEl = container.querySelector(ERROR);

    if (errorEl) {
      errorEl.insertAdjacentElement(
        'afterend',
        panel
      );
    } else {
      container.append(panel);
    }

    form.addEventListener('submit', async event => {
      event.preventDefault();

      if (busy || !form.reportValidity()) {
        return;
      }

      const zipInput = container.querySelector(INPUT);
      const zip = zipInput?.value.trim() || '';

      if (!/^\d{5}$/.test(zip)) {
        status.textContent =
          'Please enter a valid ZIP code above.';
        return;
      }

      busy = true;
      button.disabled = true;

      status.textContent =
        'Submitting your request…';

      try {
        const cart = await cartPayload(zip);

        const result = await submit({
          type: 'delivery_request',
          eventId: newId(),
          zip,
          items: cart.items,
          subtotalCents: cart.subtotalCents,
          currency: cart.currency,
          name: name.value.trim(),
          email: email.value.trim(),
          phone: phone.value.trim(),
          consent: consent.checked
        });

        if (result.success) {
          // Clear, accessible success confirmation; no change to submission logic.
          status.replaceChildren();
          status.style.marginTop = '16px';

          const confirmation = document.createElement('span');
          confirmation.style.cssText = `
            display: block;
            color: #276738;
            font-size: 15px;
            font-weight: 600;
            line-height: 1.5;
          `;
          confirmation.textContent =
            '✓ Thank you! Your request has been received.';

          const followUp = document.createElement('span');
          followUp.style.cssText = `
            display: block;
            margin-top: 4px;
            color: #30211b;
            font-size: 14px;
            font-weight: 400;
            line-height: 1.5;
          `;
          followUp.textContent = "We'll be in touch shortly.";

          status.append(confirmation, followUp);

          name.disabled = true;
          email.disabled = true;
          phone.disabled = true;
          consent.disabled = true;
        }
      } catch (_) {
        status.textContent =
          'We could not submit your request. ' +
          'Please try again or email ' +
          'info@savoyandco.com.';

        button.disabled = false;
      } finally {
        busy = false;
      }
    });

    return panel;
  }

  async function check() {
    const container =
      document.querySelector(CONTAINER);

    if (!container) {
      lastAttemptKey = '';
      return;
    }

    const input = container.querySelector(INPUT);
    const error = container.querySelector(ERROR);
    const success = container.querySelector('.bcdp-success-msg');
    const panel = document.getElementById(BOX_ID);

    // Bird removes or replaces the error node after a successful lookup.
    // Never require an error element before clearing an old form.
    if (!input) {
      panel?.remove();
      lastAttemptKey = '';
      return;
    }

    const zip = input.value.trim();
    const isVisible = el => {
      if (!el || !el.isConnected || !el.getClientRects().length) return false;
      const style = window.getComputedStyle(el);
      return style.display !== 'none' && style.visibility !== 'hidden';
    };

    const available = isVisible(success) &&
      /local delivery available/i.test(success.textContent || '');

    // A current success message always overrides a previous rejection.
    if (available) {
      panel?.remove();
      lastAttemptKey = '';
      return;
    }

    const rejected = /^\d{5}$/.test(zip) &&
      isVisible(error) &&
      /local delivery not available/i.test(error.textContent || '');

    if (!rejected) {
      panel?.remove();
      lastAttemptKey = '';
      return;
    }

    buildPanel(container);

    // Record once per ZIP and cart fingerprint
    // during this page load.
    try {
      const cart = await cartPayload(zip);

      // The ZIP or Bird's result may have changed during the fetch.
      if (input.value.trim() !== zip) return;
      if (!cart.items.length) return;

      const fingerprint = JSON.stringify([
        zip,
        cart.items,
        cart.subtotalCents
      ]);

      if (
        fingerprint === lastAttemptKey ||
        seen.has(fingerprint)
      ) {
        return;
      }

      lastAttemptKey = fingerprint;
      seen.add(fingerprint);

      const result = await submit({
        type: 'zip_rejected',
        eventId: newId(),
        zip,
        items: cart.items,
        subtotalCents: cart.subtotalCents,
        currency: cart.currency
      });

      console.info(
        '[Savoy Tracker] Rejected ZIP recorded',
        result
      );
    } catch (error) {
      console.warn(
        '[Savoy Tracker] ZIP tracking unavailable:',
        error.message
      );
    }
  }

  function scheduleCheck() {
    if (pendingCheck) return;

    pendingCheck = true;

    setTimeout(() => {
      pendingCheck = false;
      check();
    }, 400);
  }

  const observer = new MutationObserver(scheduleCheck);

  function start() {
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      characterData: true,
      attributes: true,
      attributeFilter: ['class', 'style', 'hidden', 'aria-hidden']
    });

    document.addEventListener(
      'click',
      scheduleCheck,
      true
    );

    document.addEventListener(
      'input',
      event => {
        if (event.target?.matches?.(INPUT)) {
          // Never leave an outdated request form visible while editing.
          document.getElementById(BOX_ID)?.remove();
          scheduleCheck();
        }
      },
      true
    );

    document.addEventListener('change', event => {
      if (event.target?.matches?.(INPUT)) scheduleCheck();
    }, true);

    // Some third-party widgets update their internal display without
    // emitting the DOM changes our MutationObserver catches.
    // Poll only while the customized-delivery panel is present.
    setInterval(() => {
      if (document.getElementById(BOX_ID)) scheduleCheck();
    }, 700);

    console.info('[Savoy Tracker] Loaded version 2026-10-17d');
    scheduleCheck();
  }

  if (document.readyState === 'loading') {
    document.addEventListener(
      'DOMContentLoaded',
      start
    );
  } else {
    start();
  }
})();